import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, RefreshCw } from "lucide-react";
import { activatePendingUpdate } from "@/pwa/registerServiceWorker";

/**
 * Shows two small unobtrusive prompts:
 *  1) Install PWA — when the browser fires 'beforeinstallprompt' (Chrome/Edge/Android)
 *  2) Update available — when the SW detects a newer bundle is ready
 *
 * iOS Safari does not fire beforeinstallprompt, so we skip the install prompt there
 * and rely on the native Add-to-Home-Screen affordance.
 */
export default function PWAPrompts() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Only show if not previously dismissed
      const dismissed = localStorage.getItem("pwa_install_dismissed_at");
      if (!dismissed || Date.now() - parseInt(dismissed, 10) > 14 * 24 * 60 * 60 * 1000) {
        setShowInstall(true);
      }
    };
    
    const onAppInstalled = () => {
      setShowInstall(false);
      setDeferredPrompt(null);
      localStorage.setItem("pwa_installed_at", String(Date.now()));
    };
    
    const onUpdate = () => {
      setUpdateAvailable(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    window.addEventListener("pwa-update-available", onUpdate);
    
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
      window.removeEventListener("pwa-update-available", onUpdate);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch (_e) {
      // ignore
    }
    setDeferredPrompt(null);
    setShowInstall(false);
  };

  const handleDismissInstall = () => {
    setShowInstall(false);
    localStorage.setItem("pwa_install_dismissed_at", String(Date.now()));
  };

  const handleApplyUpdate = () => {
    activatePendingUpdate();
    setUpdateAvailable(false);
  };

  return (
    <>
      {showInstall && (
        <div
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-[360px] z-[9999] bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 flex items-start gap-3 animate-in slide-in-from-bottom-4"
          data-testid="pwa-install-prompt"
        >
          <div className="w-10 h-10 rounded-xl bg-sage-100 flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5 text-sage-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800">Install ConnectEd</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Add it to your home screen for faster loads and offline access.
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                onClick={handleInstall}
                className="bg-sage-600 hover:bg-sage-700 h-8 text-xs"
                data-testid="pwa-install-btn"
              >
                Install
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismissInstall}
                className="h-8 text-xs text-slate-500"
                data-testid="pwa-install-dismiss"
              >
                Not now
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismissInstall}
            className="text-slate-400 hover:text-slate-600 -mt-1 -mr-1 p-1"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {updateAvailable && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] bg-slate-900 text-white rounded-full shadow-2xl px-4 py-2 flex items-center gap-3 animate-in slide-in-from-bottom-4"
          data-testid="pwa-update-prompt"
        >
          <RefreshCw className="w-4 h-4" />
          <span className="text-xs">A new version is ready.</span>
          <Button
            size="sm"
            onClick={handleApplyUpdate}
            className="h-7 px-3 text-xs bg-sage-500 hover:bg-sage-600"
            data-testid="pwa-update-btn"
          >
            Refresh
          </Button>
        </div>
      )}
    </>
  );
}
